// ---------------------------------------------------------------------------
// Beziehungen (PLAN.md §16c): Ein Element steht zu einem anderen in einer Beziehung aus einem festen Katalog – Familie, Ehe, Liebe,
// Freundschaft, Feindschaft, Hierarchie, Mitgliedschaft –, gültig ab bzw. bis zu einer Szene. Gespeichert wird sie einmal, beim
// Element, von dem sie ausgeht (`Entity.relations`); die Gegenrichtung („Tochter von“ zu „Mutter von“) ergibt sich. Die Bezeichnung
// folgt dem Geschlecht: aus dem Feld „Geschlecht“ (`gender`), sonst aus dem Wort, mit dem die Beziehung angelegt wurde.
//
// Dazu (AP-30): die Haltung je Richtung (zugetan, neutral, abgeneigt) mit Wendepunkten ab einer Szene, die Verwandtschaft, die sich aus
// Eltern, Geschwistern und Ehen ergibt (Großeltern, Onkel, Cousinen, Schwager …), und – zwischen zwei Orten – der Weg mit seiner
// Reisezeit (AP-40).
// ---------------------------------------------------------------------------

import type { StoryCtx } from './ctx.js';
import { narrativeOrder, worldOrder } from './narrative.js';
import { isScene } from './scene.js';
import { RELATION_KINDS, type Attitude, type Relation, type RelationAttitude, type RelationForm, type RelationKind } from './types.js';

export const isRelationKind = (k: string): k is RelationKind => (RELATION_KINDS as readonly string[]).includes(k);

/** Beziehungen ohne Richtung: von beiden Seiten gleich bezeichnet */
const MUTUAL: ReadonlySet<RelationKind> = new Set(['geschwister', 'ehe', 'freundschaft', 'feindschaft', 'weg']);
export const isMutual = (kind: RelationKind): boolean => MUTUAL.has(kind);

type Words = { w: string; m: string; n: string };
const same = (n: string): Words => ({ w: n, m: n, n });

/** Bezeichnung je Art und Seite (Quelle bzw. Ziel), nach Geschlecht des Elements, das sie trägt */
const WORDS: Record<RelationKind, { source: Words; target: Words }> = {
  elternteil: { source: { w: 'Mutter von', m: 'Vater von', n: 'Elternteil von' }, target: { w: 'Tochter von', m: 'Sohn von', n: 'Kind von' } },
  geschwister: { source: { w: 'Schwester von', m: 'Bruder von', n: 'Geschwister von' }, target: { w: 'Schwester von', m: 'Bruder von', n: 'Geschwister von' } },
  ehe: { source: { w: 'Ehefrau von', m: 'Ehemann von', n: 'verheiratet mit' }, target: { w: 'Ehefrau von', m: 'Ehemann von', n: 'verheiratet mit' } },
  liebe: { source: same('liebt'), target: same('geliebt von') },
  freundschaft: { source: { w: 'Freundin von', m: 'Freund von', n: 'befreundet mit' }, target: { w: 'Freundin von', m: 'Freund von', n: 'befreundet mit' } },
  feindschaft: { source: { w: 'Feindin von', m: 'Feind von', n: 'verfeindet mit' }, target: { w: 'Feindin von', m: 'Feind von', n: 'verfeindet mit' } },
  vorgesetzt: { source: { w: 'Vorgesetzte von', m: 'Vorgesetzter von', n: 'vorgesetzt' }, target: { w: 'Untergebene von', m: 'Untergebener von', n: 'untergeben' } },
  mitglied: { source: same('Mitglied von'), target: same('hat als Mitglied') },
  weg: { source: same('Weg nach'), target: same('Weg nach') },
};

/** Name der Art (für Auswahl und Übersicht) */
export const RELATION_NAMES: Record<RelationKind, string> = {
  elternteil: 'Eltern und Kind',
  geschwister: 'Geschwister',
  ehe: 'Ehe',
  liebe: 'Liebe',
  freundschaft: 'Freundschaft',
  feindschaft: 'Feindschaft',
  vorgesetzt: 'Hierarchie',
  mitglied: 'Mitglied',
  weg: 'Reiseweg',
};

/** Bezeichnung einer Beziehung aus Sicht der Seite `side`, nach dem Geschlecht dessen, der sie trägt („Mutter von“, „Sohn von“) */
export const relationWord = (kind: RelationKind, side: 'source' | 'target', form: RelationForm | null): string => WORDS[kind][side][form ?? 'n'];

const GENDER: Record<string, RelationForm> = { weiblich: 'w', männlich: 'm', w: 'w', m: 'm', female: 'w', male: 'm' };

/** Eine Beziehung mit dem Element, an dem sie steht (Quelle) */
export interface RelationEntry {
  sourceId: string;
  relation: Relation;
}

/** Wer an einer Beziehung beteiligt ist, unabhängig von der Richtung, wenn sie keine hat */
const pairKey = (sourceId: string, kind: RelationKind, targetId: string): string =>
  isMutual(kind) && targetId < sourceId ? `${kind}:${targetId}:${sourceId}` : `${kind}:${sourceId}:${targetId}`;

const entriesCache = new WeakMap<StoryCtx, RelationEntry[]>();

/**
 * Alle Beziehungen der Geschichte: die eigenen und – in einer Serie – die aus früheren Episoden, die am Ende der vorigen gelten
 * (`relation.inherited`: ihre Episode). Steht hier selbst eine derselben Art zwischen denselben beiden, gilt die eigene.
 */
export function relationEntries(ctx: StoryCtx): RelationEntry[] {
  const hit = entriesCache.get(ctx);
  if (hit) return hit;
  const own = ctx.story.entities.flatMap((e) => (e.relations ?? []).map((relation) => ({ sourceId: e.id, relation })));
  const taken = new Set(own.map((x) => pairKey(x.sourceId, x.relation.kind, x.relation.targetId)));
  const prior = (ctx.story.series?.relations ?? []).filter((p) => ctx.entities.has(p.sourceId) && !taken.has(pairKey(p.sourceId, p.relation.kind, p.relation.targetId)));
  const out = [...own, ...prior.map((p) => ({ sourceId: p.sourceId, relation: p.relation }))];
  entriesCache.set(ctx, out);
  return out;
}

/** Geschlecht eines Elements: aus dem Feld „Geschlecht“ (`gender`), sonst aus den Wörtern, mit denen Beziehungen angelegt wurden. */
export function genderOf(ctx: StoryCtx, entityId: string): RelationForm | null {
  const e = ctx.entities.get(entityId);
  const field = e?.fields.gender;
  if (typeof field === 'string' && GENDER[field.toLowerCase()]) return GENDER[field.toLowerCase()]!;
  for (const { sourceId, relation: r } of relationEntries(ctx)) {
    if (sourceId === entityId && r.forms?.source) return r.forms.source;
    if (r.targetId === entityId && r.forms?.target) return r.forms.target;
  }
  return null;
}

/** Eine Beziehung aus Sicht eines Elements */
export interface RelationView {
  relation: Relation;
  /** das Element, an dem sie steht (Quelle) */
  sourceId: string;
  /** das andere Element */
  otherId: string;
  /** auf welcher Seite das betrachtete Element steht */
  side: 'source' | 'target';
  /** „Mutter von“, „verheiratet mit“ – ohne den Namen des anderen */
  word: string;
}

/** Alle Beziehungen eines Elements, in beiden Richtungen (ohne die zu gelöschten Elementen). */
export function relationsOf(ctx: StoryCtx, entityId: string): RelationView[] {
  const own = genderOf(ctx, entityId);
  const out: RelationView[] = [];
  for (const { sourceId, relation: r } of relationEntries(ctx)) {
    if (!ctx.entities.has(r.targetId)) continue;
    if (sourceId === entityId) out.push({ relation: r, sourceId, otherId: r.targetId, side: 'source', word: relationWord(r.kind, 'source', own) });
    else if (r.targetId === entityId) out.push({ relation: r, sourceId, otherId: sourceId, side: 'target', word: relationWord(r.kind, 'target', own) });
  }
  return out;
}

/** Stelle einer Szene in Weltzeit (für „gültig ab/bis“); -1: gibt es nicht */
const worldIndex = (ctx: StoryCtx): Map<string, number> => new Map(worldOrder(ctx, ctx.story.nodes.filter(isScene)).map((s, i) => [s.id, i]));

/** Gilt die Beziehung zur Zeit der Szene (Weltzeit)? Grenzen, deren Szene es nicht mehr gibt, zählen nicht. */
export function relationActiveAt(r: Relation, at: number, world: Map<string, number>): boolean {
  const from = r.from ? world.get(r.from) : undefined;
  const to = r.to ? world.get(r.to) : undefined;
  return (from === undefined || from <= at) && (to === undefined || at <= to);
}

/** Beziehungen eines Elements, die zur Zeit der Szene gelten (Element-Kontext beim Schreiben); ohne Szene: alle. */
export function relationsAt(ctx: StoryCtx, entityId: string, sceneId: string | null): RelationView[] {
  const all = relationsOf(ctx, entityId);
  if (!sceneId) return all;
  const world = worldIndex(ctx);
  const at = world.get(sceneId);
  return at === undefined ? all : all.filter((v) => relationActiveAt(v.relation, at, world));
}

/** „Kapitel.Szene Titel“ bzw. der Titel einer Szene für „ab …“/„bis …“ */
export function sceneLabel(ctx: StoryCtx, sceneId: string): string {
  const scene = ctx.nodes.get(sceneId);
  if (!scene) return '';
  const entry = narrativeOrder(ctx).scenes.find((s) => s.scene.id === sceneId);
  const title = String(scene.fields.title ?? '').trim() || 'Szene';
  return entry ? `${entry.label} ${title}` : title;
}

const isPlace = (ctx: StoryCtx, id: string): boolean => {
  const e = ctx.entities.get(id);
  return !!e && ctx.types.get(e.typeId)?.category === 'place';
};

/** Prüft eine Beziehung, bevor sie an `sourceId` gespeichert wird; liefert eine Meldung oder null. */
export function checkRelation(ctx: StoryCtx, sourceId: string, r: Pick<Relation, 'kind' | 'targetId' | 'from' | 'to' | 'minutes' | 'attitude' | 'turns'>): string | null {
  if (!isRelationKind(r.kind)) return `Unbekannte Art von Beziehung: „${r.kind}“.`;
  if (!ctx.entities.has(r.targetId)) return 'Die andere Entität der Beziehung gibt es nicht.';
  if (r.targetId === sourceId) return 'Eine Beziehung braucht zwei verschiedene Entitäten.';
  for (const s of [r.from, r.to]) if (s && !isScene(ctx.nodes.get(s))) return '„Ab“ und „bis“ einer Beziehung sind Szenen.';
  if (r.kind === 'weg') {
    if (!isPlace(ctx, sourceId) || !isPlace(ctx, r.targetId)) return 'Ein Weg verbindet zwei Orte.';
    if (!r.minutes || r.minutes < 1) return 'Ein Weg braucht eine Reisezeit.';
    if (r.attitude || r.turns?.length) return 'Ein Weg hat keine Haltung.';
  } else if (r.minutes !== undefined) return 'Eine Reisezeit gibt es nur bei einem Weg.';
  for (const t of r.turns ?? []) if (!isScene(ctx.nodes.get(t.sceneId))) return 'Ein Wendepunkt liegt in einer Szene.';
  return null;
}

/** Gibt es dieselbe Beziehung schon (gleiche Art zwischen denselben beiden; ohne Richtung auch umgekehrt)? */
export function hasRelation(ctx: StoryCtx, sourceId: string, kind: RelationKind, targetId: string): boolean {
  const at = (from: string, to: string): boolean => !!ctx.entities.get(from)?.relations?.some((r) => r.kind === kind && r.targetId === to);
  return at(sourceId, targetId) || (isMutual(kind) && at(targetId, sourceId));
}

// ---------------------------------------------------------------------------
// Haltung je Richtung und Wendepunkte (AP-30)
// ---------------------------------------------------------------------------

/** Bezeichnung einer Haltung („zugetan“, „neutral“, „abgeneigt“) */
export const ATTITUDE_NAMES: Record<Attitude, string> = { zugetan: 'zugetan', neutral: 'neutral', abgeneigt: 'abgeneigt' };

/**
 * Haltung einer Beziehung zur Zeit der Szene an Stelle `at` (Weltzeit): die Haltung zu Beginn, darüber die Wendepunkte bis einschließlich
 * dieser Szene (in Weltzeit geordnet; je Richtung gilt der letzte, der sie nennt). Ohne `at`: die Haltung zu Beginn.
 */
export function attitudeAt(r: Relation, at: number | undefined, world: ReadonlyMap<string, number>): RelationAttitude {
  const out: RelationAttitude = { ...(r.attitude ?? {}) };
  if (at === undefined) return out;
  const turns = (r.turns ?? []).flatMap((t) => {
    const w = world.get(t.sceneId);
    return w !== undefined && w <= at ? [{ t, w }] : [];
  });
  for (const { t } of turns.sort((a, b) => a.w - b.w)) Object.assign(out, t.attitude);
  return out;
}

/** Die Wendepunkte einer Beziehung in Weltzeit (Wendepunkte in Szenen, die es nicht mehr gibt, fallen weg) */
export function turnsInOrder(ctx: StoryCtx, r: Relation): NonNullable<Relation['turns']> {
  const world = worldIndex(ctx);
  return (r.turns ?? []).filter((t) => world.has(t.sceneId)).sort((a, b) => world.get(a.sceneId)! - world.get(b.sceneId)!);
}

/** Haltung aus Sicht des Elements auf Seite `side`: seine eigene zum anderen und die des anderen zu ihm */
export const ownAttitude = (a: RelationAttitude, side: 'source' | 'target'): { own?: Attitude; other?: Attitude } =>
  side === 'source' ? { own: a.source, other: a.target } : { own: a.target, other: a.source };

/** Die Stelle jeder Szene in Weltzeit (für Haltung zu einer Szene) */
export const worldPositions = (ctx: StoryCtx): Map<string, number> => worldIndex(ctx);

// ---------------------------------------------------------------------------
// Berechnete Verwandtschaft (AP-30): aus Eltern, Geschwistern und Ehen
// ---------------------------------------------------------------------------

/** Art einer berechneten Verwandtschaft (aus Sicht des Elements, um das es geht) */
export type KinKind = 'geschwister' | 'halbgeschwister' | 'großelternteil' | 'enkel' | 'urgroßelternteil' | 'urenkel' | 'onkel' | 'neffe' | 'cousin' | 'schwiegerelternteil' | 'schwiegerkind' | 'schwager';

const KIN_WORDS: Record<KinKind, Words> = {
  geschwister: { w: 'Schwester von', m: 'Bruder von', n: 'Geschwister von' },
  halbgeschwister: { w: 'Halbschwester von', m: 'Halbbruder von', n: 'Halbgeschwister von' },
  großelternteil: { w: 'Großmutter von', m: 'Großvater von', n: 'Großelternteil von' },
  enkel: { w: 'Enkelin von', m: 'Enkel von', n: 'Enkelkind von' },
  urgroßelternteil: { w: 'Urgroßmutter von', m: 'Urgroßvater von', n: 'Urgroßelternteil von' },
  urenkel: { w: 'Urenkelin von', m: 'Urenkel von', n: 'Urenkelkind von' },
  onkel: { w: 'Tante von', m: 'Onkel von', n: 'Tante bzw. Onkel von' },
  neffe: { w: 'Nichte von', m: 'Neffe von', n: 'Nichte bzw. Neffe von' },
  cousin: { w: 'Cousine von', m: 'Cousin von', n: 'Cousine bzw. Cousin von' },
  schwiegerelternteil: { w: 'Schwiegermutter von', m: 'Schwiegervater von', n: 'Schwiegerelternteil von' },
  schwiegerkind: { w: 'Schwiegertochter von', m: 'Schwiegersohn von', n: 'Schwiegerkind von' },
  schwager: { w: 'Schwägerin von', m: 'Schwager von', n: 'verschwägert mit' },
};

/** Eine berechnete Verwandtschaft: `entityId` ist `word` `otherId` („Großmutter von Clara“), über `via` (die Glieder dazwischen) */
export interface Kin {
  kind: KinKind;
  otherId: string;
  word: string;
  via: string[];
}

/**
 * Verwandtschaft, die sich aus den eingetragenen Beziehungen ergibt (ohne `ab`/`bis`): Geschwister über gemeinsame Eltern (Halbgeschwister,
 * wenn beide zwei bekannte Eltern haben und nur einen teilen), Groß- und Urgroßeltern, Enkel, Onkel und Tanten, Nichten und Neffen,
 * Cousinen und Cousins, Schwiegereltern und -kinder, Schwager und Schwägerin. Was schon als Beziehung eingetragen ist, fehlt; je anderem
 * Element nur die nächste Verwandtschaft.
 */
export function kinshipOf(ctx: StoryCtx, entityId: string): Kin[] {
  const parents = new Map<string, Set<string>>();
  const children = new Map<string, Set<string>>();
  const siblings = new Map<string, Set<string>>();
  const spouses = new Map<string, Set<string>>();
  const link = (m: Map<string, Set<string>>, a: string, b: string): void => void (m.get(a) ?? m.set(a, new Set()).get(a)!).add(b);
  const direct = new Set<string>();
  for (const { sourceId, relation: r } of relationEntries(ctx)) {
    if (!ctx.entities.has(r.targetId)) continue;
    if (sourceId === entityId) direct.add(r.targetId);
    if (r.targetId === entityId) direct.add(sourceId);
    if (r.kind === 'elternteil') {
      link(parents, r.targetId, sourceId);
      link(children, sourceId, r.targetId);
    } else if (r.kind === 'geschwister') {
      link(siblings, sourceId, r.targetId);
      link(siblings, r.targetId, sourceId);
    } else if (r.kind === 'ehe') {
      link(spouses, sourceId, r.targetId);
      link(spouses, r.targetId, sourceId);
    }
  }
  const of = (m: Map<string, Set<string>>, id: string): string[] => [...(m.get(id) ?? [])];
  /** Geschwister: eingetragen oder über einen gemeinsamen Elternteil; `half`: nur einen von je zwei bekannten Eltern geteilt */
  const siblingsOf = (id: string): { id: string; half: boolean; via: string[] }[] => {
    const out = new Map<string, { id: string; half: boolean; via: string[] }>();
    for (const s of of(siblings, id)) out.set(s, { id: s, half: false, via: [] });
    const mine = of(parents, id);
    for (const p of mine)
      for (const s of of(children, p)) {
        if (s === id || out.has(s)) continue;
        const theirs = of(parents, s);
        const shared = mine.filter((x) => theirs.includes(x));
        out.set(s, { id: s, half: mine.length === 2 && theirs.length === 2 && shared.length === 1, via: shared });
      }
    return [...out.values()];
  };
  const found = new Map<string, Kin>();
  const own = genderOf(ctx, entityId);
  const add = (kind: KinKind, otherId: string, via: string[]): void => {
    if (otherId === entityId || direct.has(otherId) || found.has(otherId)) return;
    found.set(otherId, { kind, otherId, word: KIN_WORDS[kind][own ?? 'n'], via });
  };
  // nach Nähe: zuerst Geschwister, dann Großeltern und Enkel, Onkel und Neffen, Cousins, Verschwägerte, zuletzt Urgroßeltern
  for (const s of siblingsOf(entityId)) add(s.half ? 'halbgeschwister' : 'geschwister', s.id, s.via);
  for (const p of of(parents, entityId)) for (const g of of(parents, p)) add('enkel', g, [p]);
  for (const c of of(children, entityId)) for (const g of of(children, c)) add('großelternteil', g, [c]);
  for (const p of of(parents, entityId)) for (const u of siblingsOf(p)) add('neffe', u.id, [p]);
  for (const s of siblingsOf(entityId)) for (const n of of(children, s.id)) add('onkel', n, [s.id]);
  for (const p of of(parents, entityId)) for (const u of siblingsOf(p)) for (const c of of(children, u.id)) add('cousin', c, [p, u.id]);
  for (const sp of of(spouses, entityId)) for (const p of of(parents, sp)) add('schwiegerkind', p, [sp]);
  for (const c of of(children, entityId)) for (const sp of of(spouses, c)) add('schwiegerelternteil', sp, [c]);
  for (const sp of of(spouses, entityId)) for (const s of siblingsOf(sp)) add('schwager', s.id, [sp]);
  for (const s of siblingsOf(entityId)) for (const sp of of(spouses, s.id)) add('schwager', sp, [s.id]);
  for (const p of of(parents, entityId)) for (const g of of(parents, p)) for (const u of of(parents, g)) add('urenkel', u, [p, g]);
  for (const c of of(children, entityId)) for (const g of of(children, c)) for (const u of of(children, g)) add('urgroßelternteil', u, [c, g]);
  return [...found.values()];
}

// ---------------------------------------------------------------------------
// Reisezeit zwischen zwei Orten (AP-40): über die eingetragenen Wege, auch über Zwischenorte (kürzester Weg)
// ---------------------------------------------------------------------------

const travelCache = new WeakMap<StoryCtx, Map<string, Map<string, number>>>();

/** Kürzeste Reisezeit in Minuten von `from` nach `to` über die Wege der Bibliothek; null, wenn kein Weg sie verbindet */
export function travelMinutes(ctx: StoryCtx, from: string, to: string): number | null {
  if (from === to) return 0;
  let all = travelCache.get(ctx);
  if (!all) travelCache.set(ctx, (all = new Map()));
  let dist = all.get(from);
  if (!dist) {
    const edges = new Map<string, { to: string; minutes: number }[]>();
    const edge = (a: string, b: string, minutes: number): void => void (edges.get(a) ?? edges.set(a, []).get(a)!).push({ to: b, minutes });
    for (const { sourceId, relation: r } of relationEntries(ctx))
      if (r.kind === 'weg' && r.minutes && ctx.entities.has(r.targetId)) {
        edge(sourceId, r.targetId, r.minutes);
        edge(r.targetId, sourceId, r.minutes);
      }
    // Dijkstra (wenige Orte: eine einfache Liste genügt)
    dist = new Map([[from, 0]]);
    const open = new Set([from]);
    while (open.size > 0) {
      let cur = '';
      for (const id of open) if (cur === '' || dist.get(id)! < dist.get(cur)!) cur = id;
      open.delete(cur);
      for (const x of edges.get(cur) ?? []) {
        const d = dist.get(cur)! + x.minutes;
        if (d < (dist.get(x.to) ?? Infinity)) {
          dist.set(x.to, d);
          open.add(x.to);
        }
      }
    }
    all.set(from, dist);
  }
  return dist.get(to) ?? null;
}

/** Reisezeit lesbar: „45 Min.“, „3 Std.“, „2 Std. 30 Min.“, „2 Tage“, „1 Tag 6 Std.“ */
export function durationText(minutes: number): string {
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  const m = minutes % 60;
  const parts = [d > 0 ? (d === 1 ? '1 Tag' : `${d} Tage`) : '', h > 0 ? `${h} Std.` : '', m > 0 && d === 0 ? `${m} Min.` : ''].filter(Boolean);
  return parts.join(' ') || '0 Min.';
}
